import type { UseQueryResult } from '@tanstack/react-query';
import { Button } from '@react-apps/design-system';
import { HintSelect } from './HintSelect';
import type { QuestionFilterOptions } from '../../domain/entities/content.entity';
import { UFS } from '../../domain/entities/content.entity';
import { useInstitutionsById } from '../hooks/use-institutions';
import { useQuestionFilterCascade } from '../hooks/use-question-search';
import {
  changeArea,
  changeInstitution,
  changeSpecialty,
  changeTheme,
  changeUf,
  changeYear,
  type QuestionSearchFilters as Filters,
} from '../question-search-filters';

const OPTIONS_ERROR = 'Não foi possível carregar as opções.';

/**
 * O estado de um select que depende de uma consulta de opções: sem o pai, à
 * espera dele; carregando; com erro; sem opções; ou livre.
 */
function levelState(
  query: UseQueryResult<QuestionFilterOptions>,
  missingParent: string | undefined,
  noOptions: string,
): { disabled: boolean; hint?: string; error?: string } {
  if (missingParent) return { disabled: true, hint: missingParent };
  if (query.isError) return { disabled: true, error: OPTIONS_ERROR };
  if (!query.data) return { disabled: true, hint: 'Carregando…' };
  if (query.data.subjects.length === 0) {
    return { disabled: true, hint: noOptions };
  }
  return { disabled: false };
}

const toOptions = (subjects: { id: string; name: string }[] | undefined) =>
  (subjects ?? []).map((s) => ({ value: s.id, label: s.name }));

/**
 * O valor aplicado sempre tem uma opção para aparecer. Quando a consulta de
 * opções falha, o filtro que veio da URL continua valendo na lista (não dá
 * para conferir o que não carregou), e sem esta opção o select mostraria
 * "Todas" ao lado de uma lista filtrada.
 */
function withCurrent(
  options: { value: string; label: string }[],
  current: string,
  failed: boolean,
): { value: string; label: string }[] {
  // Carregando, o select está desabilitado com "Carregando…"; a opção só
  // nasce quando a consulta falhou e o valor não pode ser conferido.
  if (!failed || !current || options.some((o) => o.value === current)) {
    return options;
  }
  return [...options, { value: current, label: 'Filtro aplicado' }];
}

/**
 * Os filtros da busca de questões (DESEN-42/43). Controlado por props, para
 * servir às duas telas: Buscar questões e Gestão de comentários.
 *
 * - **Grande Área → Especialidade → Tema**, em cascata: cada nível pede ao
 *   back os filhos do anterior e fica desabilitado, dizendo o que falta, até o
 *   pai ser escolhido. Trocar um nível limpa os de baixo.
 * - **UF → Instituição**, como na busca de provas: só as vivas da UF.
 * - **Ano**, das opções da raiz (os anos das provas reais).
 * - Se uma consulta de opções falha, o select dela mostra o erro, com
 *   "Tentar de novo", e a lista segue com os outros filtros. O filtro que já
 *   estava aplicado continua visível ("Filtro aplicado"), e "Limpar filtros"
 *   o tira.
 *
 * Os três níveis do assunto são `Select` nativo. `ponytail:` se o Tema
 * passar de algumas dezenas de opções, o `Combobox` nasce no design system.
 *
 * A primeira opção de cada select ("Todas"/"Todos") é o "sem filtro": o
 * `placeholder` do `Select` é uma opção desabilitada, e com ele não daria para
 * voltar atrás.
 */
export function QuestionSearchFilters({
  value,
  onChange,
}: {
  value: Filters;
  onChange: (next: Filters) => void;
}) {
  const institutions = useInstitutionsById();
  const { areas, specialties, themes } = useQuestionFilterCascade(value);

  const area = levelState(areas, undefined, 'Sem Grandes Áreas cadastradas');
  const specialty = levelState(
    specialties,
    value.area ? undefined : 'Escolha a Grande Área primeiro',
    'Sem Especialidades nesta Grande Área',
  );
  const theme = levelState(
    themes,
    value.specialty ? undefined : 'Escolha a Especialidade primeiro',
    'Sem Temas nesta Especialidade',
  );

  const institutionOptions = value.uf
    ? [...(institutions.data?.values() ?? [])]
        .filter((i) => !i.deletedAt && i.uf === value.uf)
        .sort((a, b) => a.initials.localeCompare(b.initials, 'pt'))
        .map((i) => ({ value: i.id, label: `${i.initials} – ${i.name}` }))
    : [];

  const failed = [areas, specialties, themes, institutions].filter(
    (q) => q.isError,
  );

  return (
    <div className="space-y-2">
      {/* Duas linhas em 5 colunas: Grande Área (1), Especialidade (2) e
          Tema (2); UF (1), Instituição (3) e Ano (1). Abaixo de `lg`, um
          select por linha. */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-5">
        <div className="lg:col-span-1">
          <HintSelect
            label="Grande Área"
            value={value.area}
            {...area}
            onChange={(e) => onChange(changeArea(value, e.target.value))}
            options={withCurrent(
              [
                { value: '', label: 'Todas' },
                ...toOptions(areas.data?.subjects),
              ],
              value.area,
              areas.isError,
            )}
          />
        </div>
        <div className="lg:col-span-2">
          <HintSelect
            label="Especialidade"
            value={value.specialty}
            {...specialty}
            onChange={(e) => onChange(changeSpecialty(value, e.target.value))}
            options={withCurrent(
              [
                { value: '', label: 'Todas' },
                ...toOptions(
                  value.area ? specialties.data?.subjects : undefined,
                ),
              ],
              value.specialty,
              specialties.isError,
            )}
          />
        </div>
        <div className="lg:col-span-2">
          <HintSelect
            label="Tema"
            value={value.theme}
            {...theme}
            onChange={(e) => onChange(changeTheme(value, e.target.value))}
            options={withCurrent(
              [
                { value: '', label: 'Todos' },
                ...toOptions(
                  value.specialty ? themes.data?.subjects : undefined,
                ),
              ],
              value.theme,
              themes.isError,
            )}
          />
        </div>
        <div className="lg:col-span-1">
          <HintSelect
            label="UF"
            value={value.uf}
            onChange={(e) => onChange(changeUf(value, e.target.value))}
            options={[
              { value: '', label: 'Todas' },
              ...UFS.map((uf) => ({ value: uf, label: uf })),
            ]}
          />
        </div>
        <div className="lg:col-span-3">
          <HintSelect
            label="Instituição"
            value={value.institutionId}
            disabled={
              !value.uf || institutions.isLoading || institutions.isError
            }
            hint={
              !value.uf
                ? 'Escolha a UF primeiro'
                : institutions.isLoading
                  ? 'Carregando…'
                  : undefined
            }
            // Sem condição de UF: a lista roda sempre, e o "Tentar de novo"
            // abaixo não pode aparecer sem a mensagem que o explica.
            error={institutions.isError ? OPTIONS_ERROR : undefined}
            onChange={(e) => onChange(changeInstitution(value, e.target.value))}
            options={withCurrent(
              [{ value: '', label: 'Todas' }, ...institutionOptions],
              value.institutionId,
              institutions.isError,
            )}
          />
        </div>
        <div className="lg:col-span-1">
          <HintSelect
            label="Ano"
            value={value.year}
            disabled={!areas.data}
            // O erro da raiz já está na Grande Área: repetido aqui, o leitor de
            // tela anunciaria a mesma frase duas vezes (`role="alert"`).
            hint={!areas.data && !areas.isError ? 'Carregando…' : undefined}
            onChange={(e) => onChange(changeYear(value, e.target.value))}
            options={withCurrent(
              [
                { value: '', label: 'Todos' },
                ...(areas.data?.years ?? []).map((y) => ({
                  value: String(y),
                  label: String(y),
                })),
              ],
              value.year,
              areas.isError,
            )}
          />
        </div>
      </div>
      {failed.length > 0 && (
        <Button
          type="button"
          variant="secondary"
          size="xs"
          onClick={() => failed.forEach((q) => void q.refetch())}
        >
          Tentar de novo
        </Button>
      )}
    </div>
  );
}
